import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Fingerprint, AlarmClock, Wallet, User, Building2, ChevronsUpDown, LogOut, WifiOff } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { useAuth } from '@/hooks/useAuth';
import { useEnsureWorkspace, useWorkspace } from '@/hooks/useWorkspace';
import { cn } from '@/lib/utils';
import { PushOptInBanner } from '@/components/PushOptInBanner';
import { WorkspaceOptionList } from '@/components/WorkspaceSwitcher';

export default function Shell() {
    const { t } = useI18n();
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    useEnsureWorkspace();

    useEffect(() => {
        if (!isAuthenticated) navigate('/login', { replace: true });
    }, [isAuthenticated]);

    if (!isAuthenticated) return null;

    return (
        <div className="min-h-[100dvh] flex flex-col bg-[#faf7f4]">
            <OfflineBanner />
            <Header />
            <PushOptInBanner />
            <main className="flex-1 pb-24">
                <Outlet />
            </main>
            <BottomTabBar />
        </div>
    );
}

function Header() {
    const { t } = useI18n();
    const { currentWorkspace, workspaces, setCurrent } = useWorkspace();
    const userId = useAuth().user?._id;
    const [open, setOpen] = useState(false);

    return (
        <header className="sticky top-0 z-30 bg-[#faf7f4]/80 backdrop-blur-lg border-b border-gray-200/60 pt-safe">
            <div className="px-4 py-2.5 flex items-center justify-between gap-3 max-w-md mx-auto">
                <div className="flex items-center gap-2 shrink-0">
                    <div className="h-8 w-8 rounded-xl bg-brand flex items-center justify-center text-white">
                        <Fingerprint className="h-4 w-4" />
                    </div>
                    <span className="text-sm font-black text-gray-900">Satutim Absensi</span>
                </div>

                {workspaces.length > 0 && (
                    // Nama workspace aktif SELALU terlihat (absen tercatat di sini) — dulu
                    // terpotong 100px sehingga workspace pribadi vs kantor sulit dibedakan.
                    <div className="relative min-w-0">
                        <button
                            onClick={() => setOpen(!open)}
                            aria-label={`${t('workspace.switch')}: ${currentWorkspace?.name || '—'}`}
                            className="flex items-center gap-1.5 max-w-full px-2.5 py-1.5 rounded-full bg-white text-xs font-bold text-gray-800 hover:bg-gray-50"
                            style={{ border: '1px solid #e0d6cc' }}
                        >
                            <Building2 className="h-3.5 w-3.5 shrink-0 text-brand-700" />
                            <span className="max-w-[170px] truncate">{currentWorkspace?.name || '—'}</span>
                            {workspaces.length > 1 && <ChevronsUpDown className="h-3 w-3 shrink-0 text-gray-400" />}
                        </button>
                        {open && workspaces.length > 1 && (
                            <>
                                <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                                <div className="absolute right-0 top-full mt-1 z-50 w-[260px] max-w-[calc(100vw-2rem)] rounded-xl bg-white shadow-xl border border-gray-100 p-1">
                                    <p className="px-3 pt-2 pb-1 text-[10px] text-gray-500 leading-snug">{t('workspace.switch_hint')}</p>
                                    <WorkspaceOptionList
                                        workspaces={workspaces}
                                        currentId={currentWorkspace?._id}
                                        userId={userId}
                                        onSelect={(ws) => { setCurrent(ws); setOpen(false); }}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </header>
    );
}

function OfflineBanner() {
    const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
    const { t } = useI18n();
    useEffect(() => {
        const up = () => setOnline(true);
        const down = () => setOnline(false);
        window.addEventListener('online', up);
        window.addEventListener('offline', down);
        return () => {
            window.removeEventListener('online', up);
            window.removeEventListener('offline', down);
        };
    }, []);
    if (online) return null;
    return (
        <div className="bg-amber-100 text-amber-900 text-xs font-bold py-2 px-4 flex items-center justify-center gap-2">
            <WifiOff className="h-3.5 w-3.5" />
            {t('error.offline_banner')}
        </div>
    );
}

const TABS = [
    { to: '/', icon: Fingerprint, labelKey: 'nav.home' },
    { to: '/lembur', icon: AlarmClock, labelKey: 'nav.overtime' },
    { to: '/slip', icon: Wallet, labelKey: 'nav.payslip' },
    { to: '/akun', icon: User, labelKey: 'nav.account' },
];

function BottomTabBar() {
    const { t } = useI18n();
    return (
        <nav
            className="fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur-xl border-t border-gray-200/80 pb-safe"
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
            <div className="flex items-stretch justify-around max-w-md mx-auto">
                {TABS.map((tab) => (
                    <NavLink
                        key={tab.to}
                        to={tab.to}
                        end={tab.to === '/'}
                        className={({ isActive }) =>
                            cn(
                                'flex flex-1 flex-col items-center justify-center gap-0.5 py-2.5 transition',
                                isActive ? 'text-brand' : 'text-gray-400',
                            )
                        }
                    >
                        {({ isActive }) => (
                            <>
                                <tab.icon
                                    className={cn('h-5 w-5 transition-transform', isActive && 'scale-110')}
                                    strokeWidth={isActive ? 2.4 : 1.8}
                                />
                                <span className={cn('text-[10px]', isActive ? 'font-bold' : 'font-medium')}>{t(tab.labelKey)}</span>
                            </>
                        )}
                    </NavLink>
                ))}
            </div>
        </nav>
    );
}

export function AccountPage() {
    const { t } = useI18n();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const handleLogout = async () => {
        await logout();
        navigate('/login', { replace: true });
    };

    return (
        <div className="px-4 pt-4 max-w-md mx-auto">
            <h1 className="text-xl font-black text-gray-900 mb-4">{t('nav.account')}</h1>

            <div className="bg-white rounded-2xl p-5 mb-3" style={{ border: '1px solid #e0d6cc' }}>
                <div className="flex items-center gap-3 mb-3">
                    <div className="h-12 w-12 rounded-full bg-brand flex items-center justify-center text-white text-base font-black">
                        {(user?.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-gray-900 truncate">{user?.displayName || user?.name}</p>
                        <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    </div>
                </div>
            </div>

            <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 bg-white text-red-600 rounded-2xl px-5 py-4 text-sm font-bold hover:bg-red-50 transition"
                style={{ border: '1px solid #fecaca' }}
            >
                <LogOut className="h-4 w-4" />
                {t('auth.logout')}
            </button>
        </div>
    );
}
